import React, { useEffect, useState } from "react";
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchFinancialAssetCategory,
  fetchFinancialAssetType,
  addFinancialAssetCategoryCriteriaValue,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function FinancialAssetCategoryClassificationCriteriaAddPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [criterias, setCriterias] = useState([]);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [allValues, setAllValues] = useState([]);

  const basePath = `/strategy/${businessDomainId}/financial-asset-categories/${categoryId}`;

  useEffect(() => {
    (async () => {
      try {
        const category = await fetchFinancialAssetCategory(categoryId);
        const types = category.associatedFinancialAssetTypes || [];
        if (types.length === 0) return;
        // 全関連タイプを並列取得してマージ
        const typeDetails = await Promise.all(
          types.map((t) => fetchFinancialAssetType(t.financialAssetTypeId))
        );
        const allCriterias = typeDetails.flatMap((td) => td.classificationCriterias || []);
        const merged = typeDetails.flatMap((td) => td.classificationCriteriaValues || []);
        setCriterias(allCriterias);
        setAllValues(merged);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleCriteriaChange = (e) => {
    const name = e.target.value;
    setSelectedCriteria(name);
    setSelectedValue("");
    setCriteriaValues(
      allValues.filter((v) => v.classificationCriteria?.classificationCriteriaName === name)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCriteria || !selectedValue) {
      notify.error(t("itCategory.classificationCriteria.selectRequired"));
      return;
    }
    try {
      const value = criteriaValues.find((v) => v.classificationCriteriaValueName === selectedValue);
      if (!value) return;
      const res = await addFinancialAssetCategoryCriteriaValue(categoryId, value);
      if (res?.result === "NG") {
        notify.error(res.errorMessage || t("message.create.error"));
        return;
      }
      notify.success(t("message.create.success"));
      navigate(`${basePath}/classification-criteria`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("financialAssetCategory.classificationCriteriaValues")}：{t("common.add")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("itCategory.classificationCriteriaName")}</InputLabel>
          <Select value={selectedCriteria} onChange={handleCriteriaChange}
            label={t("itCategory.classificationCriteriaName")}>
            {criterias.map((c) => (
              <MenuItem key={c.classificationCriteriaName} value={c.classificationCriteriaName}>
                {c.classificationCriteriaName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required sx={{ mb: 3 }} disabled={!selectedCriteria}>
          <InputLabel>{t("itCategory.classificationCriteriaValueName")}</InputLabel>
          <Select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)}
            label={t("itCategory.classificationCriteriaValueName")}>
            {criteriaValues.map((v) => (
              <MenuItem key={v.classificationCriteriaValueName} value={v.classificationCriteriaValueName}>
                {v.classificationCriteriaValueName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/classification-criteria`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
